import { ProtectedRoute } from '@shared/auth';
import { parseInt } from 'lodash';
import React, { Suspense, useState } from 'react';
import { HiDownload } from 'react-icons/hi';
import { LiaCalendar } from 'react-icons/lia';

import { TableLoadingOverlay } from '@/components/loading';
import { Button } from '@/components-new/button';
import { EmptyState, EmptyStateBody, EmptyStateHeading } from '@/components-new/empty-state';
import { SectionDescription, SectionHeading } from '@/components-new/section';
import { SegmentedControl } from '@/components-new/segmented-control';
import { TextLink } from '@/components-new/text';
import { MeetingPrepDrawer } from '@/features/ai/components/meeting-prep-drawer';
import { MeetingDetailsDrawer, MeetingsList } from '@/features/calendar/components/meetings';
import { Meeting } from '@/features/calendar/types/meeting';
import { useDrug } from '@/features/drugs/api/use-drug';
import { useDrugMeetings, exportDrugMeetings } from '@/features/drugs/api/use-drug-meetings';
import { useUserSettings } from '@/features/user-settings/api/api';
import { useCurrentUser } from '@/hooks/use-current-user';
import { useParams } from '@/router';
import { drugName } from '@/utils/drugs';
import { useFeatures } from '@/shared/feature-flags/use-features';

const DrugMeetingsPage = () => {
  const { id } = useParams('/drugs/:id/meetings');
  const { data: drug } = useDrug(id);

  const [meetingsFilter, setMeetingsFilter] = useState<string>('all');
  const [timeFilter, setTimeFilter] = useState<'upcoming' | 'previous'>('upcoming');

  const {
    data: meetings,
    isLoading: isMeetingsLoading,
  } = useDrugMeetings({
    drugId: parseInt(id),
    filters: {
      priority: meetingsFilter === 'priority',
      timeRange: timeFilter
    }
  });

  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | undefined>();
  const [aiBriefMeetingId, setAiBriefMeetingId] = useState<number | undefined>();
  const drugDisplayName = drugName(drug);

  const showMeetingDetails = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
  };

  const handleExport = async () => {
    try {
      await exportDrugMeetings(parseInt(id), {
        priority: meetingsFilter === 'priority',
        timeRange: timeFilter
      });
    } catch (error) {
      console.error('Failed to export meetings:', error);
    }
  };


  const { user: currentUser } = useCurrentUser();
  const isActiveUser = currentUser.client == undefined || currentUser.client?.isActive;
  const isAiEnabled = currentUser.premiumFeatures.includes('ai_insights') && isActiveUser;
  const { data: userSettings } = useUserSettings();
  const hasPriorityStates = userSettings.priorityStates.length > 0;

  const { getFeature } = useFeatures();
  const isDrugCalendarEnhancementsFeatureEnabled = getFeature('drugCalendarEnhancements');

  const meetingsEmptyState = !hasPriorityStates ? (
    <EmptyState>
      <LiaCalendar className="size-12 text-gray-500"/>
      <EmptyStateHeading>No priority states</EmptyStateHeading>
      <EmptyStateBody>
        You don't have any priority states set up. Go to your <TextLink href="/user-settings">settings</TextLink> to add some.
      </EmptyStateBody>
    </EmptyState>
  ) : <EmptyState>
    <LiaCalendar className="size-12 text-gray-500"/>
    <EmptyStateHeading>No {timeFilter} meetings</EmptyStateHeading>
    <EmptyStateBody>
      There are no {timeFilter} meetings for {drugDisplayName}.
    </EmptyStateBody>
  </EmptyState>;

  return (
    <div className="mt-4">
      <div className="mb-6">
        {isDrugCalendarEnhancementsFeatureEnabled &&
          <SegmentedControl
            className="mb-4 mt-2"
            items={[{ label: 'Upcoming', value: 'upcoming' }, { label: 'Previous', value: 'previous' }]}
            onChange={(value) => setTimeFilter(value as 'upcoming' | 'previous')}
            value={timeFilter}
          />
        }

        <div className="flex items-center justify-between">
          <div>
            <SectionHeading level={2}>
              {timeFilter === 'upcoming' ? 'Upcoming' : 'Previous'} Meetings
            </SectionHeading>
            <SectionDescription>
              {timeFilter === 'upcoming' ? 'Upcoming' : 'Previous'} meetings pertaining to {drugDisplayName} or its classification(s).
            </SectionDescription>
          </div>

          <div className="flex items-center gap-4">
            {currentUser.client && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">Show:</span>
                <SegmentedControl
                  className="text-xs [&>button]:px-2 [&>button]:py-1.5 [&>button]:text-xs"
                  items={[{ label: 'All States', value: 'all' }, { label: 'Priority States', value: 'priority' }]}
                  onChange={setMeetingsFilter}
                  value={meetingsFilter}
                />
              </div>
            )}

            {isDrugCalendarEnhancementsFeatureEnabled &&
              <Button
                color="secondary"
                onClick={handleExport}
                disabled={isMeetingsLoading}
              >
                <HiDownload />
                Export
              </Button>
            }
          </div>
        </div>
      </div>

      <div className="relative mt-4 min-h-20 lg:col-span-7 xl:col-span-8">
        <MeetingsList
          isLoading={isMeetingsLoading}
          meetings={meetings}
          emptyState={meetingsEmptyState}
          onShowMeetingDetails={showMeetingDetails}
          isAiEnabled={isAiEnabled}
          onShowAiBrief={(meeting) => setAiBriefMeetingId(meeting.id)}
        />
      </div>

      <MeetingDetailsDrawer
        meeting={selectedMeeting}
        onClose={() => setSelectedMeeting(undefined)}
      />

      {isAiEnabled && aiBriefMeetingId && (
        <MeetingPrepDrawer
          meetingId={aiBriefMeetingId}
          open={!!aiBriefMeetingId}
          onClose={() => setAiBriefMeetingId(undefined)}
        />
      )}
    </div>
  );
};

const DrugMeetingsPageWrapper = () => {
  return (
    <ProtectedRoute policies={['canViewCalendar']}>
      <Suspense fallback={<TableLoadingOverlay/>}>
        <DrugMeetingsPage/>
      </Suspense>
    </ProtectedRoute>
  );
};

export default DrugMeetingsPageWrapper;

function atures() {
    throw new Error('Function not implemented.');
}
